import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Fab,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add, Phone } from '@mui/icons-material';
import { toast } from 'react-toastify';
import LeadsTable from '../components/leads/LeadsTable';
import LeadForm from '../components/leads/LeadForm';
import AssignLeadDialog from '../components/leads/AssignLeadDialog';
import LeadsFilter from '../components/leads/LeadsFilter';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import FollowUpDialog from '../components/followup/FollowUpDialog';
import {
  fetchLeads,
  createLead,
  updateLead,
  assignLead,
} from '../store/slices/leadsSlice';
import { fetchUsers, fetchStates, fetchDiseases } from '../store/slices/usersSlice';
import { callsService } from '../services/calls';
import { leadsService } from '../services/leads';

const Leads = () => {
  const dispatch = useDispatch();
  const { leads, loading, error, pagination } = useSelector((state) => state.leads);
  const { user } = useSelector((state) => state.auth);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpMode, setFollowUpMode] = useState('update');
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchLeads(filters));
    dispatch(fetchUsers());
    dispatch(fetchStates());
    dispatch(fetchDiseases());
  }, [dispatch, filters]);

  const handleCreateLead = async (leadData) => {
    try {
      await dispatch(createLead(leadData)).unwrap();
      setCreateDialogOpen(false);
      showSnackbar('Lead created successfully!', 'success');
    } catch (error) {
      showSnackbar('Failed to create lead', 'error');
    }
  };

  const handleEditLead = async (formData) => {
    try {
      console.log('Leads: Form data received:', formData);
      console.log('Leads: Selected lead data:', selectedLead);
      
      // Merge form data with existing lead data to ensure all required fields are present
      const completeLeadData = {
        // Start with existing lead data
        ...selectedLead,
        // Override with form data
        ...formData,
        // Ensure required fields are always present
        name: formData.name || selectedLead.name,
        phone: formData.phone || selectedLead.phone,
        city: formData.city || selectedLead.city,
        disease: formData.disease || selectedLead.disease,
        // Handle the lead_source field mapping
        lead_source: formData.lead_source || selectedLead.source || selectedLead.lead_source
      };
      
      console.log('Leads: Complete lead data being sent:', completeLeadData);
      
      await dispatch(updateLead({ 
        id: selectedLead.id, 
        leadData: completeLeadData 
      })).unwrap();
      setEditDialogOpen(false);
      setSelectedLead(null);
      showSnackbar('Lead updated successfully!', 'success');
    } catch (error) {
      console.error('Lead update error:', error);
      showSnackbar(error.message || 'Failed to update lead', 'error');
    }
  };

  const handleAssignLead = async (assignmentData) => {
    try {
      console.log('Assignment data:', assignmentData);
      console.log('Selected lead:', selectedLead);
      
      await dispatch(assignLead({
        leadId: selectedLead.id,
        assignedTo: assignmentData.assigned_to,
        reason: assignmentData.reason,
      })).unwrap();
      
      setAssignDialogOpen(false);
      setSelectedLead(null);
      showSnackbar('Lead assigned successfully!', 'success');
    } catch (error) {
      console.error('Assignment error:', error);
      showSnackbar(`Failed to assign lead: ${error.message || error}`, 'error');
    }
  };

  const handleCallLead = async (phoneNumber, leadId) => {
    try {
      await callsService.initiateCall(phoneNumber, leadId);
      showSnackbar('Call initiated!', 'success');
    } catch (error) {
      showSnackbar('Failed to initiate call', 'error');
    }
  };

  const handleAddComment = async (leadId, commentData) => {
    try {
      await leadsService.addLeadComment(leadId, commentData);
      showSnackbar('Comment added successfully!', 'success');
      // Refresh the leads data to show the new comment
      dispatch(fetchLeads(filters));
    } catch (error) {
      showSnackbar('Failed to add comment', 'error');
      console.error('Failed to add comment:', error);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage + 1 });
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters({ ...filters, page: 1, page_size: pageSize });
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...newFilters, page: 1 });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setEditDialogOpen(true);
  };

  const openAssignDialog = (lead) => {
    setSelectedLead(lead);
    setAssignDialogOpen(true);
  };

  const handleFollowUpLead = (lead) => {
    setSelectedLead(lead);
    setFollowUpMode('update');
    setFollowUpDialogOpen(true);
  };

  const handleScheduleCall = (lead) => {
    setSelectedLead(lead);
    setFollowUpMode('schedule');
    setFollowUpDialogOpen(true);
  };

  const handleFollowUpSuccess = () => {
    dispatch(fetchLeads(filters));
    showSnackbar('Follow-up updated successfully!', 'success');
  };


  if (loading && leads.length === 0) {
    return <LoadingSpinner message="Loading leads..." />;
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <div>
          <Typography variant="h4" gutterBottom>
            Leads
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track your leads
          </Typography>
        </div>
        
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Lead
          </Button>
        )}
      </Box>

      {error && (
        <ErrorAlert 
          error={error} 
          onClose={() => dispatch({ type: 'leads/clearError' })} 
        />
      )}

      {/* Filters */}
      <LeadsFilter 
        onFiltersChange={handleFilterChange}
        initialFilters={filters}
      />

      <LeadsTable
        leads={leads}
        loading={loading}
        totalCount={pagination.count}
        page={pagination.page - 1}
        pageSize={pagination.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        onEditLead={openEditDialog}
        onAssignLead={openAssignDialog}
        onCallLead={handleCallLead}
        onAddComment={handleAddComment}
        onFollowUpLead={handleFollowUpLead}
        onScheduleCall={handleScheduleCall}
      />

      {/* Floating Action Button for Mobile */}
      {(user?.role === 'admin' || user?.role === 'manager') && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <Add />
        </Fab>
      )}

      {/* Dialogs */}
      <LeadForm
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateLead}
        title="Create New Lead"
      />

      <LeadForm
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedLead(null);
        }}
        onSubmit={handleEditLead}
        lead={selectedLead}
        title="Edit Lead"
      />

      <AssignLeadDialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setSelectedLead(null);
        }}
        onSubmit={handleAssignLead}
        lead={selectedLead}
      />

      <FollowUpDialog
        open={followUpDialogOpen}
        onClose={() => {
          setFollowUpDialogOpen(false);
          setSelectedLead(null);
        }}
        lead={selectedLead}
        mode={followUpMode}
        onSuccess={handleFollowUpSuccess}
      />


      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Leads;
